import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  Paper,
} from '@mui/material';
import { Mic, Pause, Play, Square, AlertTriangle } from 'lucide-react';
import { toast } from '../../../../../../../ui/toast';
import { NotebookIcons } from '../../../../../../../icons/icons';
import { Dialog, NameEmailAvatar } from '../../../../../../../ui';
import { ICandidate } from '../../../../../../../utils/types/JobOpenings';
import { DIALOG_TYPE } from '../../../../../Constants/DialogTypes';

export interface IRecordInterviewDialog {
  open: boolean;
  handleCloseRecordDialog: () => void;
  onRecordInterview?: (row: ICandidate, audioBlob: Blob) => void;
  onOpenPanelistNotes?: (row: ICandidate) => void;
  candidate: ICandidate;
}

export const RecordInterviewDialog: React.FC<IRecordInterviewDialog> = (props) => {
  const {
    open,
    handleCloseRecordDialog,
    onRecordInterview,
    onOpenPanelistNotes,
    candidate,
  } = props;
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [confirmSave, setConfirmSave] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [hasStartedOnce, setHasStartedOnce] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const timerInterval = useRef<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    };
  }, []);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  const handleSaveRecording = async () => {
    if (!onRecordInterview || !audioBlob || !confirmSave || isSaving) return;

    setIsSaving(true);
    const duration = recordingTime;
    const toastId = toast.info(
      `Saving recording (${formatTime(duration)}). This might take a moment...`,
      {
        position: 'bottom-center',
        autoClose: false,
        closeButton: false,
      },
    );

    try {
      await onRecordInterview(candidate, audioBlob);
      toast.update(toastId, {
        render: 'Recording saved successfully!',
        type: toast.TYPE.SUCCESS,
        autoClose: 3000,
      });
      handleClose();
    } catch (error) {
      toast.update(toastId, {
        render: 'Failed to save recording. Please try again.',
        type: toast.TYPE.ERROR,
        autoClose: 5000,
      });
      setIsSaving(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        if (timerInterval.current) {
          clearInterval(timerInterval.current);
        }
      };

      mediaRecorder.current.start(1000);
      setIsRecording(true);
      setHasStartedOnce(true);
      setIsPaused(false);

      timerInterval.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.pause();
      setIsPaused(true);
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.resume();
      setIsPaused(false);
      timerInterval.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      mediaRecorder.current.stream.getTracks().forEach((track) => track.stop());
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    }
  };

  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    setAudioBlob(null);
    setConfirmSave(false);
    setRecordingTime(0);
    setHasStartedOnce(false);
    setIsSaving(false);
    handleCloseRecordDialog();
  };

  return (
    <Dialog
      isOpen={open}
      title='Record Interview'
      handleApply={handleSaveRecording}
      cancelButtonLable='Cancel'
      confirmButtonLable={isSaving ? 'Saving...' : 'Save Recording'}
      disabled={!audioBlob || !confirmSave || isSaving}
      dialogType={DIALOG_TYPE.ERROR}
      onClose={handleClose}
    >
      <Stack spacing={2} sx={{ p: 2 }}>
        <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>
          Candidate
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <NameEmailAvatar
            avatar=''
            email={candidate.email || 'Unknown Email'}
            name={`${candidate.firstName || 'Unknown'} ${
              candidate.lastName || 'Candidate'
            }`}
          />
        </Box>
        <Stack alignItems='center' spacing={2}>
          <Typography variant='h5' sx={{ fontWeight: 600 }}>
            {formatTime(recordingTime)}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            {!isRecording ? (
              <IconButton
                onClick={startRecording}
                title='Start Recording'
                sx={{
                  p: 1.5,
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <Mic size={24} />
              </IconButton>
            ) : (
              <>
                {!isPaused ? (
                  <IconButton
                    onClick={pauseRecording}
                    title='Pause Recording'
                    sx={{
                      p: 1.5,
                      bgcolor: 'warning.main',
                      color: 'warning.contrastText',
                      '&:hover': { bgcolor: 'warning.dark' },
                    }}
                  >
                    <Pause size={24} />
                  </IconButton>
                ) : (
                  <IconButton
                    onClick={resumeRecording}
                    title='Resume Recording'
                    sx={{
                      p: 1.5,
                      bgcolor: 'success.main',
                      color: 'success.contrastText',
                      '&:hover': { bgcolor: 'success.dark' },
                    }}
                  >
                    <Play size={24} />
                  </IconButton>
                )}
                <IconButton
                  onClick={stopRecording}
                  title='Stop Recording'
                  sx={{
                    p: 1.5,
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                    '&:hover': { bgcolor: 'error.dark' },
                  }}
                >
                  <Square size={24} />
                </IconButton>
              </>
            )}
            <IconButton
              onClick={() => onOpenPanelistNotes?.(candidate)}
              title='Open Panelist Notes'
              disabled={isSaving}
              sx={{
                p: 1.5,
                bgcolor: 'success.dark',
                color: 'success.contrastText',
                '&:hover': { bgcolor: 'success.main' },
              }}
            >
              <NotebookIcons />
            </IconButton>
          </Box>
        </Stack>

        {hasStartedOnce && !isRecording && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {audioBlob ? (
              <>
                <Box
                  component='audio'
                  controls
                  src={URL.createObjectURL(audioBlob)}
                  sx={{ width: '100%' }}
                />
                <Paper
                  variant='outlined'
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 2,
                    bgcolor: 'warning.light',
                    borderColor: 'warning.main',
                  }}
                >
                  <AlertTriangle
                    size={20}
                    style={{ color: '#d97706', flexShrink: 0, marginTop: 4 }}
                  />
                  <Stack spacing={1}>
                    <Typography variant='body2' sx={{ color: 'warning.contrastText' }}>
                      Please review your recording before saving. Once saved,
                      you will not be able to modify it.
                      {recordingTime > 300 && (
                        <Typography
                          component='span'
                          variant='body2'
                          sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}
                        >
                          Note: This is a {Math.round(recordingTime / 60)} minute
                          recording and may take a few moments to save.
                        </Typography>
                      )}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={confirmSave}
                          onChange={(e) => setConfirmSave(e.target.checked)}
                          disabled={isSaving}
                          size='small'
                        />
                      }
                      label={
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>
                          I confirm that I have reviewed the recording and it is
                          ready to be saved
                        </Typography>
                      }
                    />
                  </Stack>
                </Paper>
              </>
            ) : (
              <Typography
                variant='body2'
                sx={{ textAlign: 'center', color: 'text.secondary' }}
              >
                Recording completed. Click the microphone icon to start a new
                recording.
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
};

export default RecordInterviewDialog;
